import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  reduxForm,
  arrayPush,
  getFormValues,
  getFormSyncErrors,
} from 'redux-form';
import { connect } from 'react-redux';

import { size, forEach, isEmpty } from 'lodash';

import * as entitiesFeaturesActions from 'store/services/entitiesFeatures/actions';
import * as entitiesDictionariesSelectors from 'store/services/entitiesDictionaries/selectors';
import * as dictionariesActions from 'store/services/dictionaries/actions';
import * as dictionariesSelectors from 'store/services/dictionaries/selectors';
import * as entitiesActions from 'store/services/entities/actions';

import { validate } from 'store/utils/entities';
import { getFeatures } from 'store/services/features/selectors';
import { entityType, connectedDictionaryType, dictionaryType } from 'types';

import FORM_NAMES from 'constants/formNames';

import InspectButton from 'components/InspectButton';
import InspectLoader from 'components/InspectLoader';
import Form from 'components/Form';
import InspectFooter from 'components/InspectFooter';
import InspectTabs from 'components/InspectTabs';

import { createReduxFormInitialValues } from './utils';

import EntityInspectHeader from './components/EntityInspectHeader';
import EntityInspectContent from './components/EntityInspectContent';
import EntityInspectGeneral from './components/EntityInspectGeneral';

import './style.scss';

export class EntityInspect extends Component {
  static propTypes = {
    getEntity: PropTypes.func.isRequired,
    getFeatures: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    partialExport: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    addField: PropTypes.func,
    allDictionaries: PropTypes.arrayOf(connectedDictionaryType),
    change: PropTypes.func,
    changeTab: PropTypes.func,
    connectedDictionaries: PropTypes.arrayOf(connectedDictionaryType),
    currentEntity: entityType,
    deleteEntity: PropTypes.func,
    dirty: PropTypes.bool,
    dirtyOnInit: PropTypes.bool,
    featureTypes: PropTypes.arrayOf(dictionaryType),
    formValues: entityType,
    getAllDictionaries: PropTypes.func,
    handleSubmit: PropTypes.func,
    invalid: PropTypes.bool, // need it on re-init when dirty is false
    isLoadingAllDictionaries: PropTypes.bool,
    isValidFeatures: PropTypes.bool,
    selectedTabIndex: PropTypes.number,
    showTabs: PropTypes.bool,
    onClose: PropTypes.func,
    onItemDeselect: PropTypes.func,
  };

  static defaultProps = {
    showTabs: true,
    dirtyOnInit: false,
    isValidFeatures: false,
    formValues: {},
    onItemDeselect: () => {},
  };

  state = {
    isLoading: false,
  };

  componentDidMount = async () => {
    const { id } = this.props;

    if (id) {
      this.handleLoad(id);
    }

    this.isMount = true;
  };

  componentWillReceiveProps = ({ id }) => {
    if (id && this.props.id !== id) {
      this.handleLoad(id);
    }
  };

  componentWillUnmount() {
    this.props.onItemDeselect();
    this.isMount = false;
  }

  getInspectTabs = () => {
    const hasWarning = !size(this.props.formValues.dictionary_connections);
    const tabs = [
      { label: 'General', tabIndex: 1 },
      { label: 'Content', tabIndex: 2 },
    ];

    if (!hasWarning) {
      return tabs;
    }

    return tabs.map(tab =>
      tab.tabIndex === 2
        ? {
            ...tab,
            hasWarning,
          }
        : tab,
    );
  };

  handleSubmit = async () => {
    this.setState({ isLoading: true });

    await this.props.onSubmit();

    // when the entity is created via rules, after sumbit the component
    // is not mounted, so we cannot setState
    if (this.isMount) {
      this.setState({ isLoading: false });
    }
  };

  handleNameInputRef = el => {
    this.nameInputRef = el;
  };

  focusNameInput = () => {
    if (this.nameInputRef) {
      this.nameInputRef.blur();
      this.nameInputRef.focus();
    }
  };

  handleLoad = async id => {
    this.setState({ isLoading: true });

    await this.props.getEntity(id);
    await this.props.getFeatures();

    this.setState({ isLoading: false }, this.focusNameInput());
  };

  handleDelete = async () => {
    await this.props.deleteEntity(this.props.id);
    this.props.onClose();
  };

  addFeature = () => {
    this.props.addField(FORM_NAMES.ENTITIES.INSPECT, 'features', {
      autoFocus: true,
    });

    this.scrollToBottom();
  };

  connectDictionary = () => {
    this.props.addField(
      FORM_NAMES.ENTITIES.INSPECT,
      'dictionary_connections',
      {},
    );
    this.scrollToBottom();
  };

  // TODO: use zenscroll (somehow) to scroll to last field
  scrollToBottom() {
    setTimeout(() => {
      this.wrapper.scrollTop = this.wrapper.scrollHeight;
    }, 100);
  }

  handleContainerRef = ref => {
    this.wrapper = ref;
  };

  // make all features private when entity turns to private
  handleToggle = value => {
    forEach(this.props.formValues.features, (feature, index) => {
      this.props.change(`features['${index}'].public`, value);
    });
  };

  handlePartialExport = () => {
    this.props.partialExport(`entity-${this.props.currentEntity.name}`, [
      this.props.id,
    ]);
  };

  renderGeneralTab = () => {
    const { selectedTabIndex, featureTypes, currentEntity } = this.props;
    const isDisabled = currentEntity.xpack_resource || currentEntity.archived;

    if (selectedTabIndex === 2) {
      return null;
    }

    return (
      <EntityInspectGeneral
        currentEntity={currentEntity}
        featureTypes={featureTypes}
        handlersOfNameInputRef={[this.handleNameInputRef]}
        isDisabled={isDisabled}
        onToggle={this.handleToggle}
      />
    );
  };

  renderContentTab = () => {
    const {
      selectedTabIndex,
      currentEntity,
      connectedDictionaries,
      getAllDictionaries,
      allDictionaries,
      formValues,
      isLoadingAllDictionaries,
    } = this.props;

    if (selectedTabIndex === 2) {
      return (
        <EntityInspectContent
          allDictionaries={allDictionaries}
          currentEntity={currentEntity}
          formValues={formValues}
          getAllDictionaries={getAllDictionaries}
          isLoadingAllDictionaries={isLoadingAllDictionaries}
          items={connectedDictionaries}
          onConnect={this.connectDictionary}
        />
      );
    }

    return null;
  };

  render() {
    const {
      selectedTabIndex,
      changeTab,
      showTabs,
      currentEntity,
      currentEntity: { archived, xpack_resource },
      onClose,
      handleSubmit,
      dirty,
      invalid,
      dirtyOnInit,
      isValidFeatures,
    } = this.props;

    const { isLoading } = this.state;

    const disabled = !!(
      (!dirty && !dirtyOnInit) ||
      isLoading ||
      archived ||
      invalid ||
      (xpack_resource && selectedTabIndex === 1) ||
      isValidFeatures
    );

    // showTabs should not be shown when entity is created via c.rules
    return (
      <div data-test="entity-inspect-window" styleName="inspect">
        {showTabs && (
          <InspectTabs
            changeTab={changeTab}
            closeTabs={onClose}
            icon="icon-entities"
            item={currentEntity}
            selectedTabIndex={selectedTabIndex}
            tabs={this.getInspectTabs()}
          />
        )}

        <EntityInspectHeader
          currentEntity={currentEntity}
          selectedTabIndex={selectedTabIndex}
          onAddFeature={this.addFeature}
          onConnectDictionary={this.connectDictionary}
          onDelete={this.handleDelete}
        />

        <div
          ref={this.handleContainerRef}
          className="entity-ispect-wrapper"
          styleName="wrapper"
        >
          <Form styleName="form" onSubmit={handleSubmit(this.handleSubmit)}>
            <InspectLoader isLoading={isLoading} />
            {this.renderGeneralTab()}
            {this.renderContentTab()}

            <InspectFooter>
              <InspectButton
                isFooter
                data-test="export-entity-button"
                disabled={xpack_resource}
                onClick={this.handlePartialExport}
              >
                Export
              </InspectButton>
              <InspectButton
                isFooter
                isSave
                data-test="save-entity-button"
                disabled={disabled}
                type="submit"
              >
                Save
              </InspectButton>
            </InspectFooter>
          </Form>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  initialValues: createReduxFormInitialValues(props.currentEntity),
  connectedDictionaries: entitiesDictionariesSelectors.getEntitiesDictionariesList(
    state,
  ),
  allDictionaries: dictionariesSelectors.getAllDictionaries(state),
  isLoadingAllDictionaries: dictionariesSelectors.getIsLoadingAll(state),
  featureTypes: getFeatures(state),
  formValues: getFormValues(FORM_NAMES.ENTITIES.INSPECT)(state),
  isValidFeatures: getFormSyncErrors('EntityInspect')(state).features
    ? getFormSyncErrors('EntityInspect')(state).features.findIndex(
        o => !isEmpty(o),
      ) !== -1
    : false,
});

const mapDispatchToProps = {
  partialExport: entitiesActions.partialExportSingleEntity,
  getEntity: entitiesActions.getEntity,
  deleteEntity: entitiesActions.deleteEntity,
  onCheckEntityName: entitiesActions.checkEntityName,
  getFeatures: entitiesFeaturesActions.getEntitiesFeaturesList,
  getAllDictionaries: dictionariesActions.getAllDictionaries,
  addField: arrayPush,
};

const asyncValidate = (formProps, dispatch, props) =>
  props.onCheckEntityName('name', formProps.name, props.currentEntity.id);

const EntityInspectConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    asyncValidate,
    validate,
    asyncChangeFields: ['name'],
    destroyOnUnmount: false,
    enableReinitialize: true,
    form: FORM_NAMES.ENTITIES.INSPECT,
    touchOnChange: true,
  })(EntityInspect),
);

export default EntityInspectConnected;
